import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import AdminContainer from '../../containers/Admin';
import LivePinTracker from '../../components/LivePin/LivePinTracker';
import { ApiService } from '../../service/api.service';
import { MASTER_URL } from '../../common/common';
import { VehicleWithLivePin } from '../../service/interfaces/LivePin.interface';

const LivePinTracking: React.FC = () => {
    const navigate = useNavigate();
    const apiService = new ApiService();

    const [vehicles, setVehicles] = useState<VehicleWithLivePin[]>([]);
    const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshInterval, setRefreshInterval] = useState<number>(60000); // 1 minute

    useEffect(() => {
        fetchVehiclesWithLivePin();
    }, []);

    const fetchVehiclesWithLivePin = async () => {
        try {
            setLoading(true);
            setError(null);

            // This would need to be implemented in your backend to return vehicles with LivePin URLs
            const response: any = await apiService.getApi(
                `${MASTER_URL}/vehicle/get-vehicles-with-livepin`
            );

            if (response.status === "200") {
                // Handle paginated response structure
                const data = response.body.data || response.body;
                const vehiclesWithLivePin = data
                    .filter((vehicle: any) => vehicle.trackingUrl)
                    .map((vehicle: any) => ({
                        id: vehicle.vehicleId,
                        vehicleName: vehicle.vehicleName || vehicle.vehicleType,
                        vehicleNumber: vehicle.vehicleRegNo || vehicle.vehicleNumber,
                        livePinUrl: vehicle.trackingUrl
                    }));

                setVehicles(vehiclesWithLivePin);

                // Auto-select first vehicle if available
                if (vehiclesWithLivePin.length > 0) {
                    setSelectedVehicle(vehiclesWithLivePin[0].id);
                }
            } else {
                throw new Error('Failed to fetch vehicles');
            }
        } catch (err) {
            console.error('Error fetching vehicles:', err);
            setError('Failed to load vehicles with LivePin URLs');
        } finally {
            setLoading(false);
        }
    };

    const handleRefreshIntervalChange = (interval: number) => {
        setRefreshInterval(interval);
    };

    const selectedVehicleData = vehicles.find(v => v.id === selectedVehicle);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                <span className="ml-4 text-gray-600">Loading vehicles...</span>
            </div>
        );
    }

    return (
        <div>
            <Header title="LivePin Vehicle Tracking" />

            <AdminContainer>
                <div className="p-6">
                    {/* Error Display */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                            <div className="flex items-center">
                                <div className="text-red-500 mr-2">⚠️</div>
                                <div>
                                    <h4 className="text-red-800 font-medium">Error</h4>
                                    <p className="text-red-600 text-sm">{error}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Controls */}
                    <div className="mb-6 flex justify-between items-center">
                        <div className="flex items-center space-x-4">
                            <label className="text-sm font-medium text-gray-700">
                                Refresh Interval:
                            </label>
                            <select
                                value={refreshInterval}
                                onChange={(e) => handleRefreshIntervalChange(Number(e.target.value))}
                                className="border border-gray-300 rounded-lg px-3 py-1 text-sm"
                            >
                                <option value={10000}>10 seconds</option>
                                <option value={60000}>1 minute</option>
                                <option value={300000}>5 minutes</option>
                            </select>
                        </div>

                        <button
                            onClick={fetchVehiclesWithLivePin}
                            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                        >
                            Refresh List
                        </button>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Vehicle List */}
                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-lg shadow-md p-4">
                                <h3 className="text-lg font-semibold mb-4 text-gray-800">
                                    Vehicles with LivePin
                                </h3>

                                {vehicles.length === 0 ? (
                                    <div className="text-center py-8">
                                        <p className="text-gray-500 mb-4">No vehicles with LivePin URLs found</p>
                                        <button
                                            onClick={() => navigate('/vehicles')}
                                            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                                        >
                                            Add Vehicle
                                        </button>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {vehicles.map(vehicle => (
                                            <div
                                                key={vehicle.id}
                                                className={`p-3 border rounded-lg cursor-pointer transition-colors ${selectedVehicle === vehicle.id
                                                    ? 'bg-blue-100 border-blue-500'
                                                    : 'bg-white border-gray-200 hover:bg-gray-50'
                                                    }`}
                                                onClick={() => setSelectedVehicle(vehicle.id)}
                                            >
                                                <h4 className="font-medium text-gray-800">
                                                    {vehicle.vehicleName}
                                                </h4>
                                                <p className="text-sm text-gray-600">
                                                    {vehicle.vehicleNumber}
                                                </p>
                                                <p className="text-xs text-blue-600 mt-1">
                                                    LivePin Active
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* LivePin Tracker */}
                        <div className="lg:col-span-2">
                            {selectedVehicleData ? (
                                <LivePinTracker
                                    livePinUrl={selectedVehicleData.livePinUrl}
                                    refreshInterval={refreshInterval}
                                    onDataUpdate={(data) => {
                                        console.log('Vehicle data updated:', data);
                                    }}
                                    onError={(error) => {
                                        console.error('LivePin tracking error:', error);
                                    }}
                                />
                            ) : (
                                <div className="bg-gray-50 rounded-lg p-8 text-center">
                                    <div className="text-gray-400 mb-4">
                                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m0 0L9 7" />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-medium text-gray-800 mb-2">
                                        Select a Vehicle
                                    </h3>
                                    <p className="text-gray-600">
                                        Choose a vehicle from the list to start LivePin tracking
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </AdminContainer>
        </div>
    );
};

export default LivePinTracking;
